import React, {memo, useEffect, useRef} from 'react';
import {Tooltip} from '@mui/material';
import {ReactComponent as MoreSvg} from "../../../../assets/icons/more.svg";
import {useNavigate} from 'react-router-dom';
import {ReactComponent as PinSvg} from "../../../../assets/icons/pin.svg";
import {useTranslation} from 'react-i18next';
import {
    ActiveOptionsButton,
    ConversationItemWrapper,
    OptionsButton,
    StyledButton,
    StyledInput
} from './ConversationItem.styled';
import {ConversationResponse} from '../../../../models/models';
import {useAppConstants} from '../../../../config/appConstants';
import axios from 'axios';
import {parseError} from '../../../../services/http.service';
import {useSnackbar} from '../../../../contexts/SnackbarContext';

interface ConversationItemProps {
    setConversations: React.Dispatch<React.SetStateAction<ConversationResponse[]>>;
    setEditingId: (id: number | null) => void;
    setNewTitle: (title: string) => void;
    setSelectedConversation:  (convo: ConversationResponse | null) => void;
    setMenuAnchorEl: (el: HTMLElement | null) => void;
    convo: ConversationResponse;
    currentChatId: number | null;
    editingId: number | null;
    newTitle: string;
    isOpen: boolean;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
                                                               setEditingId,
                                                               setConversations,
                                                               setSelectedConversation,
                                                               setNewTitle,
                                                               setMenuAnchorEl,
                                                               convo,
                                                               isOpen,
                                                               newTitle,
                                                               currentChatId,
                                                               editingId
                                                           }) => {
    const {DEFAULT_CONVERSATION_TITLE_PREFIX} = useAppConstants()
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();

    const inputRef = useRef<HTMLInputElement>(null);

    const handleTitleUpdate = async (convoId: number) => {
        try {
            await axios.patch(`/chat/conversation/${convoId}/title?title=${encodeURIComponent(newTitle)}`);
            setConversations(prev =>
                prev.map(convo =>
                    convo.id === convoId ? {...convo, title: newTitle} : convo
                )
            );
            setEditingId(null);
            setNewTitle('');
        } catch (error) {
            console.error('Error updating title:', error);
            showSnackbar(parseError(error), "error");
        }
    };

    useEffect(() => {
        if (editingId === convo.id && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
    }, [editingId, convo.id]);

    return (
        <ConversationItemWrapper $active={currentChatId === convo.id}>
            <StyledButton
                open={isOpen}
                onClick={() => navigate(`/chat/${convo.id}`)}
                onDoubleClick={() => {
                    setEditingId(convo.id);
                    setNewTitle(
                        convo.title || `${DEFAULT_CONVERSATION_TITLE_PREFIX}${convo.id}`
                    );
                }}
            >
                {editingId === convo.id ? (
                    <StyledInput
                        ref={inputRef}
                        type="text"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        onBlur={() => {
                            setEditingId(null);
                            setNewTitle('');
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') handleTitleUpdate(convo.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    <>
                        {convo.pinned && (
                            <div
                                style={{
                                    marginRight: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <PinSvg
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        color: currentChatId === convo.id ? 'white' : '#474747',
                                    }}
                                />
                            </div>
                        )}
                        <span>
                                                                {convo.title || `${DEFAULT_CONVERSATION_TITLE_PREFIX}${convo.id}`}
                                                            </span>
                    </>
                )}
            </StyledButton>

            <Tooltip title={t('sidebar.showConversationOptions')}>
                {currentChatId === convo.id ? (
                    <ActiveOptionsButton
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedConversation(convo);
                            setMenuAnchorEl(e.currentTarget);
                        }}
                    >
                        <MoreSvg/>
                    </ActiveOptionsButton>
                ) : (
                    <OptionsButton
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedConversation(convo);
                            setMenuAnchorEl(e.currentTarget);
                        }}
                    >
                        <MoreSvg/>
                    </OptionsButton>
                )}
            </Tooltip>
        </ConversationItemWrapper>
    );
};

const arePropsEqual = (
    prev: ConversationItemProps,
    next: ConversationItemProps
) => {
    const isThisItemEdited = prev.editingId === prev.convo.id || next.editingId === next.convo.id;

    return (
        prev.convo.id === next.convo.id &&
        prev.convo.title === next.convo.title &&
        prev.convo.pinned === next.convo.pinned &&
        prev.currentChatId === next.currentChatId &&
        prev.isOpen === next.isOpen && (isThisItemEdited
            ? prev.editingId === next.editingId && prev.newTitle === next.newTitle
            : true)
    );
};

export default memo(ConversationItem, arePropsEqual);
